import React, { useCallback, useMemo, useState } from "react";

import { FeatureFlags, Risk } from "@design-controls/types";
import {
  createQColumnHelper,
  DataProvider,
  QBox,
  QCard,
  QCenter,
  QCollapse,
  QDataTable,
  QHeading,
  QIcon,
  QIconButton,
  QStack,
  QTag,
  QTooltip,
} from "@qualio/ui-components";
import { Link, useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { designElementStatusColumn } from "../DesignElementStatusColumn/DesignElementStatusColumn";
import { sortByCode } from "../../sortByCode";

const columnHelper = createQColumnHelper<Risk>();

type DesignElement = { latest?: string } & Risk;

type Props = {
  risks: Array<DesignElement>;
  underReview?: boolean;
  versioned?: boolean;
};

export const RisksTable: React.FC<Props> = ({
  risks,
  underReview,
  versioned = false,
}) => {
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const features = useFlags();

  const onClickToDiff = useCallback(
    (item: DesignElement) => {
      return () => navigate(`${item.id}`);
    },
    [navigate],
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.code("code", {
        header: "Code",
        minWidth: "100px",
        width: "10%",
      }),
      {
        accessorKey: "title",
        header: "Title",
        meta: {
          minWidth: "300px",
          width: "30%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return (
            <Link
              to={`../risk/${row.subType === "fmea" ? "fmea" : "iso"}/${
                row.id
              }${versioned ? `/version/${row.version}` : ""}`}
              target="_blank"
            >
              {row.title}
            </Link>
          );
        },
      },
      columnHelper.text((record) => record.category?.label, {
        header: "Category",
        minWidth: "150px",
        width: "20%",
      }),
      {
        header: "Type",
        meta: {
          minWidth: "150px",
          width: "10%",
        },
        cell: ({ row: { original: row } }: { row: { original: Risk } }) => {
          return (
            <QTag variantColor="gray">
              {!row.subType || row.subType === "iso" ? "ISO 14971" : "FMEA"}
            </QTag>
          );
        },
      },
      {
        ...designElementStatusColumn(),
        meta: {
          minWidth: "150px",
          width: "10%",
        },
      },
    ];

    if (versioned && features[FeatureFlags.COMPARE_VIEW]) {
      columns.push({
        header: " ",
        id: "diff",
        meta: {
          maxWidth: "50px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return (
            row.itemStatus !== "NEW" && (
              <QCenter>
                <QTooltip label="See difference">
                  <QBox>
                    <QIconButton
                      iconName="GitPullRequest"
                      aria-label="See difference between version"
                      onClick={onClickToDiff(row)}
                    />
                  </QBox>
                </QTooltip>
              </QCenter>
            )
          );
        },
      });
    }

    if (underReview) {
      columns.push({
        header: " ",
        meta: {
          maxWidth: "90px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return row.latest && row.version !== row.latest ? (
            <QCenter>
              <QIcon
                iconName="FillExclamationTriangleFill"
                color="orange.500"
              />
            </QCenter>
          ) : null;
        },
      });
    }

    return columns;
  }, [underReview, versioned, onClickToDiff, features]);

  if (!risks.length) {
    return null;
  }

  return (
    <QCard>
      <QStack
        direction="row"
        justifyContent="space-between"
        padding="12px 16px"
      >
        <QHeading fontSize="md" fontWeight={600}>
          Risks
        </QHeading>
        <QIconButton
          onClick={() => setExpanded((s) => !s)}
          iconName={expanded ? "ChevronUp" : "ChevronDown"}
          aria-label={expanded ? "Collapse risks" : "Expand risks"}
        />
      </QStack>
      <QCollapse isOpen={expanded}>
        <DataProvider.Fixed data={risks.sort(sortByCode)}>
          <QDataTable columns={columns} hideItemCount data-cy="risk-list" />
        </DataProvider.Fixed>
      </QCollapse>
    </QCard>
  );
};
